import React from 'react';
import type { VenueRevenuePeriodAverage } from 'generated/graphql';
import { useParams } from 'react-router-dom';
import { convertTo12HourFormat } from 'utils/dateTimeHelpers';
import { useModalContext } from 'common/Context/ModalContext';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import { SURGE_RECOMMENDATION, SURGE_RECOMMENDATION_PERCENTAGE } from './constants';
import styles from './Boost.module.scss';

const calculateEstimatedBoostForPeriod = (venueRevenueAverage: VenueRevenuePeriodAverage): number => {
  return (
    Math.round(
      venueRevenueAverage.averageTotalRevenueOrders *
        venueRevenueAverage.averageTotalNumberOfOrders *
        SURGE_RECOMMENDATION_PERCENTAGE *
        100
    ) / 100
  );
};

interface PeakProps {
  weekday: string;
  to: string;
  from: string;
  estimatedSurgeAmount: number;
  isSurchargeEnabled: boolean;
  posName: string;
  index: number;
}

const Peak: React.FC<PeakProps> = ({ weekday, to, from, estimatedSurgeAmount, isSurchargeEnabled, posName, index }) => {
  const { venueId } = useParams<{ venueId: string }>();
  const { openModal } = useModalContext();

  const handlePricingOnClick = (): void => {
    if (venueId) {
      logAmplitudeEvent({
        name: 'view boost modal',
        category: AmplitudeEventCategories.BOOST,
        properties: {
          [AmplitudeEventProperties.SOURCE]: 'high surge',
          [AmplitudeEventProperties.POS_NAME]: posName
        }
      });
      openModal({
        type: 'BOOST_PEAK_CALL_TO_ACTION',
        isWideModal: true,
        venueId
      });
    }
  };

  const formattedFrom = convertTo12HourFormat(from);
  const formattedTo = convertTo12HourFormat(to);

  return (
    <li data-testid={`boost-peak-${index}`}>
      <div className={styles.IconContainer}>
        <span className={styles.Icon}>🚀</span>
      </div>
      <div className={styles.SuggestionContainer}>
        <p className={styles.Heading}>Order Peak Time</p>
        <div className={styles.Suggestion}>
          <p>Try a dynamic price increase of {SURGE_RECOMMENDATION}%</p>
          <p>
            {weekday} Between {formattedFrom} - {formattedTo}
          </p>
        </div>
        <p className={styles.Estimated}>
          Est ${estimatedSurgeAmount} by a {SURGE_RECOMMENDATION}% increase during this time
        </p>
      </div>
      <div className={styles.ViewSurging}>
        {isSurchargeEnabled ? (
          <p className={styles.SurgeEnabled}>
            <i className="mm-check" />
            <span>Boost active</span>
          </p>
        ) : (
          <button data-testid={`boost-peak-button-${index}`} type="button" onClick={handlePricingOnClick}>
            Boost pricing
          </button>
        )}
      </div>
    </li>
  );
};

interface Props {
  peakRanges: VenueRevenuePeriodAverage[];
  isSurchargeEnabled: boolean;
  posName: string;
}

const BoostPeaks: React.FC<Props> = ({ peakRanges, isSurchargeEnabled, posName }) => {
  return (
    <div className={styles.BoostPeaksContainer}>
      <ul>
        {peakRanges.map((peakRange, index) => (
          <Peak
            key={`${peakRange.dayOfWeek}-${peakRange.periodTo}-${peakRange.periodFrom}`}
            weekday={peakRange.dayOfWeek}
            to={peakRange.periodTo}
            from={peakRange.periodFrom}
            estimatedSurgeAmount={calculateEstimatedBoostForPeriod(peakRange)}
            isSurchargeEnabled={isSurchargeEnabled}
            posName={posName}
            index={index}
          />
        ))}
      </ul>
    </div>
  );
};

export default BoostPeaks;
